/**
 * Паспорт сайта — единая точка, откуда фронт берёт домен, название,
 * счётчик Метрики и провайдера оплаты. Всё задаётся переменными сборки
 * REACT_APP_* (панель Timeweb → сборка); значения по умолчанию лежат в
 * frontend/.env и равны сегодняшнему поведению астрофил.рф, поэтому без
 * выставленных переменных сайт не меняется ни в чём.
 *
 * Второй экземпляр (astrofeel.ru) отличается только набором переменных:
 *   REACT_APP_SITE_URL=https://astrofeel.ru
 *   REACT_APP_SITE_NAME=ASTROfeel
 *   REACT_APP_METRIKA_ID=            (пусто → счётчик не грузится)
 *   REACT_APP_PAYMENT_PROVIDER=robokassa
 *   REACT_APP_STRIPE_ENABLED=1
 *
 * ВАЖНО: CRA подставляет process.env.REACT_APP_* на сборке буквально,
 * поэтому каждая переменная читается прямым обращением, без циклов.
 */

const DEFAULT_SITE_URL = 'https://астрофил.рф';
const DEFAULT_SITE_NAME = 'АСТРОфил';
const DEFAULT_METRIKA_ID = '108738698';

function clean(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/** Канонический origin сайта без завершающего слэша. */
export const SITE_URL = (clean(process.env.REACT_APP_SITE_URL) || DEFAULT_SITE_URL).replace(/\/+$/, '');

/** Хост сайта в обеих формах (unicode и punycode) — для проверок window.location.hostname. */
export const SITE_HOSTS = (() => {
  // unicode-форма — как записано в переменной; punycode — через new URL() (IDNA).
  const raw = SITE_URL.replace(/^https?:\/\//, '').split('/')[0];
  const hosts = [raw];
  try {
    hosts.push(new URL(SITE_URL).hostname);
  } catch (_) {
    /* некорректный URL — остаётся только сырая форма */
  }
  return Array.from(new Set(hosts.filter(Boolean).map((h) => h.toLowerCase())));
})();
export function isSiteHost(hostname) {
  return !!hostname && SITE_HOSTS.includes(String(hostname).toLowerCase());
}

/**
 * Путь, по которому живут статьи CMS. Задаётся переменной сборки
 * REACT_APP_ARTICLES_PATH; по умолчанию `/stranitsy` — то есть астрофил.рф
 * без выставленной переменной не меняется ни в чём.
 *
 * Второй экземпляр (astrofeel.ru) переносит 35 статей старого сайта и обязан
 * сохранить их прежние адреса `/stati/<slug>`, поэтому там задаётся
 *   REACT_APP_ARTICLES_PATH=/stati
 * и тот же адрес получают список статей, canonical, sitemap и внутренние
 * ссылки. Ту же переменную читает frontend/server.js в рантайме (серверные
 * SEO-заголовки и 301), а бэкенд — свою ARTICLES_PATH (см. routers/pages.py).
 *
 * Значение нормализуется: ведущий слэш обязателен, завершающий убирается,
 * мусор (пустая строка, пробелы, посторонние символы) откатывает к умолчанию —
 * неверно набранная переменная не должна ронять маршрутизацию сайта.
 */
const DEFAULT_ARTICLES_PATH = '/stranitsy';

export function normalizeArticlesPath(value) {
  const raw = clean(value);
  if (!raw) return DEFAULT_ARTICLES_PATH;
  const withSlash = raw.startsWith('/') ? raw : `/${raw}`;
  const trimmed = withSlash.replace(/\/+$/, '');
  return /^\/[a-z0-9][a-z0-9-]*$/i.test(trimmed) ? trimmed.toLowerCase() : DEFAULT_ARTICLES_PATH;
}

export const ARTICLES_PATH = normalizeArticlesPath(process.env.REACT_APP_ARTICLES_PATH);

/** Путь статей отличается от умолчания — значит, нужен 301 со старого. */
export const ARTICLES_PATH_IS_DEFAULT = ARTICLES_PATH === DEFAULT_ARTICLES_PATH;

/** Прежний путь статей — с него на ру ведём 301 на ARTICLES_PATH. */
export const LEGACY_ARTICLES_PATH = DEFAULT_ARTICLES_PATH;

/** Адрес статьи по слагу — единственный способ его собрать во фронте. */
export function articleUrl(slug) {
  return `${ARTICLES_PATH}/${slug}`;
}

/** Название сайта для title / og:site_name / текстов интерфейса. */
export const SITE_NAME = clean(process.env.REACT_APP_SITE_NAME) || DEFAULT_SITE_NAME;

/** Название не задано переменной — значит, сайт ведёт себя как астрофил.рф. */
export const SITE_NAME_IS_DEFAULT = SITE_NAME === DEFAULT_SITE_NAME;

/**
 * Название сайта в обычном написании — для текстов, где бренд набран не
 * капсом («Статьи — Астрофил», «программы Астрофил»). При значении по
 * умолчанию отдаёт ровно ту строку, что стоит в этих текстах сегодня,
 * поэтому на рф ничего не меняется; на ру подставляется REACT_APP_SITE_NAME.
 */
export const SITE_NAME_PLAIN = SITE_NAME_IS_DEFAULT ? 'Астрофил' : SITE_NAME;

/**
 * Составное написание бренда для шапки: «АСТРО» + «фил», «ASTRO» + «feel».
 * Делим по первой строчной букве, поэтому оба значения REACT_APP_SITE_NAME
 * разбираются одним правилом и второй точки правды не появляется.
 * Имя без строчных букв (или начинающееся со строчной) уходит в head целиком,
 * хвост тогда пустой — шапка просто рисует его одним начертанием.
 */
export function splitSiteName(name) {
  const value = clean(name) || SITE_NAME;
  let cut = 0;
  // Строчная буква — та, что отличается от своей же верхней формы.
  // Цифры, пробелы и знаки от своих форм не отличаются, на них тоже стоп.
  while (cut < value.length && value[cut] === value[cut].toUpperCase()) {
    if (value[cut] === value[cut].toLowerCase()) break;
    cut += 1;
  }
  if (cut === 0 || cut === value.length) return { head: value, tail: '' };
  return { head: value.slice(0, cut), tail: value.slice(cut) };
}

/** Готовая пара для шапки — считается один раз на модуль. */
export const SITE_NAME_PARTS = splitSiteName(SITE_NAME);

/**
 * Полное закрытие сайта от индексации. Включается строго значением "1"
 * переменной сборки REACT_APP_NOINDEX (выставляется на плашке, в репозитории
 * её нет). Любое другое значение и её отсутствие — индексация разрешена.
 * Потребители: src/components/SEO.jsx (мета-тег в SPA), scripts/postbuild.js
 * (мета-тег в статическом build/index.html) и server.js (robots.txt и
 * серверные SEO-блоки, там та же переменная читается в рантайме).
 */
export const NOINDEX = clean(process.env.REACT_APP_NOINDEX) === '1';

/**
 * Счётчик Яндекс.Метрики. Пустая строка в переменной — осознанное
 * «без счётчика» (ym() тогда не существует, вызовы защищены проверкой
 * window.ym). Не заданная переменная → счётчик рф из .env.
 */
const rawMetrika = clean(
  process.env.REACT_APP_METRIKA_ID === undefined ? DEFAULT_METRIKA_ID : process.env.REACT_APP_METRIKA_ID
);
// Не все панели умеют хранить пустое значение — «off/none/0» тоже значит «без счётчика».
export const METRIKA_ID = /^\d+$/.test(rawMetrika) && rawMetrika !== '0' ? rawMetrika : '';
export const METRIKA_ID_NUM = METRIKA_ID ? Number(METRIKA_ID) : null;

/** Провайдер основной кнопки оплаты: 'yookassa' (рф) | 'robokassa' (ру). */
export const PAYMENT_PROVIDER = (clean(process.env.REACT_APP_PAYMENT_PROVIDER) || 'yookassa').toLowerCase();
export const IS_ROBOKASSA = PAYMENT_PROVIDER === 'robokassa';

/**
 * Показывать ли на странице оплаты галочку согласия на автосписание (ру).
 *
 * ВЫКЛЮЧЕНО по умолчанию и отделено от самих списаний намеренно: Робокасса
 * проверяет сайт ДО подключения рекуррента, и форму с галочкой и условиями
 * надо показать раньше, чем уйдёт первый счёт с пометкой Recurring.
 * Парная переменная на бэкенде — ROBOKASSA_CONSENT_UI_ENABLED; сам рекуррент
 * живёт под отдельной ROBOKASSA_RECURRING_ENABLED. На астрофил.рф переменной
 * нет: там своя галочка ЮKassa, и эта её не касается.
 */
export const ROBOKASSA_CONSENT_UI = IS_ROBOKASSA && ['1', 'true', 'yes', 'on'].includes(
  clean(process.env.REACT_APP_ROBOKASSA_CONSENT_UI).toLowerCase()
);

/** Показывать ли окно «Оплата иностранной картой» (Stripe). */
export const STRIPE_ENABLED = ['1', 'true', 'yes', 'on'].includes(
  clean(process.env.REACT_APP_STRIPE_ENABLED).toLowerCase()
);

/**
 * Акция «ДВОЙНАЯ ВЫГОДА» (первый месяц ПРО за 499) есть в сборках обоих
 * сайтов (решение владелицы 30.10.2026 — сайты продают одинаково; отменяет
 * решение от 01.10.2026). Кому показывать — рублёвому пути через ЮKassa —
 * решает doubleBenefitOn() в lib/rubProvider.js; право на цену — сервер
 * (backend/double_benefit.py). Флаг остался выключателем сборки.
 */
export const DOUBLE_BENEFIT_ENABLED = true;

/** Подпись «Платежи: …» в футере и оферте. */
export const PAYMENT_METHODS_LABEL = IS_ROBOKASSA
  ? (STRIPE_ENABLED ? 'Robokassa, Stripe' : 'Robokassa')
  : 'Robokassa, ЮKassa';

/**
 * Подстановка названия сайта в готовые тексты (SEO-заголовки, описания),
 * где «АСТРОфил» означает сам сайт. При значении по умолчанию — тождество.
 */
export function brand(text) {
  if (typeof text !== 'string' || SITE_NAME === DEFAULT_SITE_NAME) return text;
  return text.replace(/АСТРОфил/g, SITE_NAME);
}

/** Адрес сайта без протокола: «астрофил.рф» / «astrofeel.ru». */
const DEFAULT_SITE_HOST = 'астрофил.рф';
export const SITE_HOST = SITE_URL.replace(/^https?:\/\//, '').split('/')[0] || DEFAULT_SITE_HOST;

/**
 * Готовый текст (строка, массив, объект src/seo/*.json) с названием и
 * адресом своего сайта: «на астрофил.рф» на ру становится «на astrofeel.ru».
 * На рф — тождество. Пара в server.js — siteDeep().
 */
export function siteText(value, host = SITE_HOST) {
  if (typeof value === 'string') {
    const named = brand(value);
    return host === DEFAULT_SITE_HOST ? named : named.split(DEFAULT_SITE_HOST).join(host);
  }
  if (Array.isArray(value)) return value.map((v) => siteText(v, host));
  if (value && typeof value === 'object') {
    const out = {};
    Object.entries(value).forEach(([k, v]) => { out[k] = siteText(v, host); });
    return out;
  }
  return value;
}
